import Axios from 'axios';
import dayjs from 'dayjs';
import _, { forEach } from 'lodash';
import * as React from 'react';
import { FormattedDate } from 'react-intl';
import { Column, UseTableCellProps } from 'react-table';
import { PredefinedFiltersEnum, SnackBarVariants, ShipmentManifestStatusEnum } from '@app/app.enum';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomDatePicker from '@app/components/shared/custom-date-picker.component';
import Icon from '@app/components/shared/icon.component';
import PredefinedFilters from '@app/components/shared/predefined-filters.component';
import Table from '@app/components/shared/table.component';
import * as models from '@app/models';
import * as constants from '@app/constants';
import Injector from '@app/services';
import { AdminShipmentService } from '@app/services/admin-shipment.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import {
	Button,
	Card,
	CardContent,
	Checkbox,
	Collapse,
	FormControl,
	Grid,
	Hidden,
	IconButton,
	ListItemText,
	MenuItem,
	TextField,
	Tooltip,
} from '@material-ui/core';
import CustomButton from '@app/components/shared/custom-button.component';
import { ButtonTypeEnum } from '@app/app.enum';
import Ribbon from '@app/components/shared/ribbon.component';

const intlService: IntlService = Injector.get(IntlService);
const adminShipmentService: AdminShipmentService = Injector.get(AdminShipmentService);
const sharedService: SharedService = Injector.get(SharedService);

type FilterType = 'startDate' | 'endDate' | 'customerVisibleAccountNumber' | 'trackingNumber';

class ShipmentManifestComponent extends React.Component<
	models.IAdminShipmentComponentProps,
	models.IShipmentManifestComponentState
> {
	private prevFiltersInfo: models.IAdminShipmentManifestPreviousFilters;
	private manifestStatusFilter: models.IAdminShipmentManifestFilters = initialAdminShipmentManifestFilter();

	constructor(props: models.IAdminShipmentComponentProps) {
		super(props);
		this.state = this.initialShipmentManifestComponentState();
	}

	public async componentDidMount() {
		try {
			this.manifestStatusFilter = initialAdminShipmentManifestFilter();

			this.prevFiltersInfo = {
				selectedManifestStatusFilterValues: [...this.manifestStatusFilter.manifestStatus],
				disabledCheckBox: { ...this.state.disabledCheckBox },
				selectAllManifestStatusSelection: this.state.selectAllManifestStatusSelection,
			};
			this.setState({
				selectedManifestStatusFilterValues: [...this.manifestStatusFilter.manifestStatus],
			});
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	public componentDidUpdate(
		prevProps: models.IAdminShipmentComponentProps,
		prevState: models.IShipmentManifestComponentState
	) {
		if (this.shouldFetchData(prevState)) {
			this.loadData();
		}
	}

	public render() {
		return (
			<React.Fragment>
				<Grid container={true} spacing={2}>
					<Grid item={true} lg={12} md={12} sm={12} xs={12}>
						<Card>
							<CardContent>
								{this.getFilterRow()}
								<Hidden only={['md', 'xs']}>
									{/* shipment manifest table*/}
									<Grid container={true}>
										<Table<models.IShipmentManifestReportVM>
											columns={this.getGridColumns()}
											data={this.state.results}
											totalPages={this.state.totalPages}
											dataLoading={this.state.dataLoading}
											currentPage={this.state.currentPage}
											canExpand={false}
											onPageChange={(e: object, page: number) => this.setState({ currentPage: page })}
											pageSize={constants.pageSize}
											totalRecords={this.state.totalResults}
										/>
									</Grid>
								</Hidden>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</React.Fragment>
		);
	}

	public filterHelperFunc(
		event: React.ChangeEvent<any>,
		rootFilterList: string[],
		stateKeyName: keyof models.IShipmentManifestComponentState,
		selectAllStateName: keyof models.IShipmentManifestComponentState
	) {
		switch (event.target.value.includes(intlService.GetFormattedMessage('app.shipment.done'))) {
			case true:
				this.prevFiltersInfo = {
					...this.prevFiltersInfo,
					[stateKeyName]: this.state[stateKeyName],
					disabledCheckBox: { ...this.state.disabledCheckBox },
					selectAllManifestStatusSelection: this.state.selectAllManifestStatusSelection,
				};
				this.performActionOnDone(stateKeyName);
				break;
			case false:
				this.applyFiltersCorrectly(event.target.value, rootFilterList, stateKeyName, selectAllStateName);
				break;
		}
	}

	private async performActionOnDone(stateKeyName: string) {
		this.setState({
			isOpenManifestStatusFilter: false,
			isFilterApplied: true,
			currentPage: 1,
		});
	}

	private applyFiltersCorrectly(
		value: string[],
		rootFilterList: string[],
		stateKeyName: keyof models.IShipmentManifestComponentState,
		selectAllStateName: keyof models.IShipmentManifestComponentState
	) {
		const isSelectAllApplied = value.includes(intlService.GetFormattedMessage('app.filter-select-all'));

		const doneState: boolean = isSelectAllApplied && !this.state[selectAllStateName];
		value = value.filter((element) => element !== intlService.GetFormattedMessage('app.filter-select-all'));
		let disabled = {};
		forEach(rootFilterList, (element) => {
			if (this.state.disabledCheckBox[element]) {
				disabled = { ...this.state.disabledCheckBox, [element]: false };
			}
		});

		disabled =
			value.length === 1
				? { ...this.state.disabledCheckBox, [value[0]]: !this.state.disabledCheckBox[value[0]] }
				: { ...this.state.disabledCheckBox, ...disabled };

		switch (doneState) {
			case true:
				value = rootFilterList;
				disabled = {
					...this.state.disabledCheckBox,
					[value[0]]: false,
				};
				break;
			case false:
				// Scenario:
				// 1.) If user checked select-all again for deselecting all filters.
				// 2.) If user de-selects the only item present inside the list.
				if (isSelectAllApplied || !value.length) {
					value = isSelectAllApplied ? value : rootFilterList;
					const temp = value[0];
					value = [];
					value.push(temp);
					disabled = {
						...this.state.disabledCheckBox,
						[value[0]]: !this.state.disabledCheckBox[value[0]],
					};
				}

				break;
		}

		// Scenarios:
		// 1.) If rootFilterList contains more than one item and both (value and rootFilterList) are of same length then SelectAll checkbox should be ticked.
		// 2.) If they they are of different lengths then SelectAll checkbox should not be ticked.
		// 3.) If rootFilterList contain only single item then SelectAll checkbox should be ticked as per the value of "doneState".
		const SelectAllCheckBoxValue =
			value.length === rootFilterList.length && rootFilterList.length > 1 ? true : doneState;

		this.setState({
			...this.state,
			[stateKeyName]: value,
			[selectAllStateName]: SelectAllCheckBoxValue,
			disabledCheckBox: disabled,
		});
	}

	private initialShipmentManifestComponentState = (): models.IShipmentManifestComponentState => {
		return {
			results: [],
			nextPage: null,
			previousPage: null,
			pageSize: 0,
			totalPages: 0,
			totalResults: 0,
			currentPage: 1,
			pageJump: 1,
			currentBalance: null,
			startDate: constants.listStartDate.toDate(),
			endDate: constants.listEndDate.toDate(),
			filterPanelExpanded: false,
			// Using Object.values for selecting all of the values in Enum object
			selectedManifestStatusFilterValues: Object.values(ShipmentManifestStatusEnum),
			selectAllManifestStatusSelection: true,
			disabledCheckBox: {},
			isOpenManifestStatusFilter: false,
			disableDatePickerFilter: false,
			dataLoading: false,
			trackingNumber: '',
			isFilterApplied: false,
		};
	};

	private getGridColumns = (): Array<Column<models.IShipmentManifestReportVM>> => {
		return [
			{
				accessor: 'trackingNumber',
				Header: intlService.GetFormattedMessage('app.manifest.tracking-number-table-header'),
				width: 210,
				disableSortBy: true,
			},
			{
				accessor: 'carrierCode',
				Header: intlService.GetFormattedMessage('app.manifest.carrier-code-table-header'),
				width: 130,
				disableSortBy: true,
			},
			{
				accessor: 'accountName',
				Header: intlService.GetFormattedMessage('app.manifest.account-name-table-header'),
				width: 230,
				disableSortBy: true,
			},
			{
				accessor: 'shipDate',
				Header: intlService.GetFormattedMessage('app.manifest.ship-date-table-header'),
				width: 90,
				disableSortBy: true,
				Cell: (tableData: UseTableCellProps<models.IShipmentManifestReportVM>) => (
					<FormattedDate value={tableData.row.original.shipDate} />
				),
			},
			{
				accessor: 'shipmentManifestStatus',
				Header: intlService.GetFormattedMessage('app.manifest.status-table-header'),
				width: 150,
				disableSortBy: true,
				Cell: (tableData: UseTableCellProps<models.IShipmentManifestReportVM>) => {
					let color: models.IRibbonProps['color'] = 'success';

					switch (tableData.row.original.shipmentManifestStatus) {
						case ShipmentManifestStatusEnum.ReadyToReattempt:
						case ShipmentManifestStatusEnum.Reattempted:
							color = 'info';
							break;

						case ShipmentManifestStatusEnum.AcceptedWithWarning:
							color = 'warning';
							break;

						case ShipmentManifestStatusEnum.Rejected:
							color = 'error';
							break;
					}

					return (
						<Ribbon
							text={tableData.row.original.shipmentManifestStatus}
							color={color}
							type='rounded'
							classNames='position-relative'
						/>
					);
				},
			},
			{
				Header: ' ',
				width: 200,
				disableSortBy: true,
				Cell: (tableData: UseTableCellProps<models.IShipmentManifestReportVM>) => {
					const [loading, setLoading] = React.useState<boolean>(false);

					const buttons = [];
					const retryButton = (
						<CustomButton
							loading={loading}
							onClick={async () => {
								try {
									setLoading(true);

									await adminShipmentService.SetupRetry(tableData.row.original.shipmentId);

									tableData.row.original.shipmentManifestStatus = ShipmentManifestStatusEnum.ReadyToReattempt;

									this.props.openSnackBar(
										intlService.GetFormattedMessage('app.manifest.retry-action-success'),
										SnackBarVariants.Success
									);
								} catch ({ data: err }) {
									if (err.statusCode < 500) {
										this.props.openSnackBar(err.errors[0].message, SnackBarVariants.Error);
									} else {
										this.props.openSnackBar(
											intlService.GetFormattedMessage('app.manifest.retry-action-failure'),
											SnackBarVariants.Error
										);
									}
								} finally {
									setLoading(false);
								}
							}}
							className='mr-1'
						>
							<Icon name='arrow-clockwise' className='mr-1' />{' '}
							{intlService.GetFormattedMessage('app.manifest.retry-button-table-data')}
						</CustomButton>
					);

					const downloadErrorButton = (
						<CustomButton onClick={() => window.open(tableData.row.original.downloadUrl)}>
							<Icon name='tray-arrow-down' className='mr-1' />
							{intlService.GetFormattedMessage('app.manifest.download-error-link-table-data')}
						</CustomButton>
					);

					const downloadWarningButton = (
						<CustomButton onClick={() => window.open(tableData.row.original.downloadUrl)}>
							<Icon name='tray-arrow-down' className='mr-1' />
							{intlService.GetFormattedMessage('app.manifest.download-warning-link-table-data')}
						</CustomButton>
					);

					switch (tableData.row.original.shipmentManifestStatus) {
						case ShipmentManifestStatusEnum.ReadyToReattempt:
						case ShipmentManifestStatusEnum.Reattempted:
							buttons.push(downloadErrorButton);
							break;

						case ShipmentManifestStatusEnum.Rejected:
							buttons.push(retryButton);
							buttons.push(downloadErrorButton);
							break;

						case ShipmentManifestStatusEnum.AcceptedWithWarning:
							buttons.push(downloadWarningButton);
							break;
					}

					return buttons;
				},
			},
		];
	};

	private getFilterRow() {
		return (
			<>
				<Grid container={true} spacing={2}>
					<Grid item={true} lg={8} md={12} sm={12} xs={12}>
						<Grid container={true} justifyContent='flex-start' spacing={2}>
							<Grid item={true} lg={4} md={4} sm={12} xs={12}>
								<PredefinedFilters
									startDate={this.state.startDate}
									endDate={this.state.endDate}
									onDateChange={this.callAfterCancellingActiveRequests(this.loadDataForPredefinedFilters)}
								/>
							</Grid>
							<Grid item={true} lg={4} md={4} sm={12} xs={12}>
								<CustomDatePicker
									label={intlService.GetFormattedMessage('app.balance-history.start-date-label')}
									maxDate={this.state.endDate}
									disabled={this.state.disableDatePickerFilter}
									value={this.state.startDate}
									onChange={(date: dayjs.Dayjs) => {
										this.setStateAfterCancellingActiveRequests('startDate', date.toDate());
									}}
								/>
							</Grid>
							<Grid item={true} lg={4} md={4} sm={12} xs={12}>
								<CustomDatePicker
									label={intlService.GetFormattedMessage('app.balance-history.end-date-label')}
									minDate={this.state.startDate}
									disabled={this.state.disableDatePickerFilter}
									value={this.state.endDate}
									onChange={(date: dayjs.Dayjs) => {
										this.setStateAfterCancellingActiveRequests('endDate', date.toDate());
									}}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item={true} lg={4} md={12} sm={12} xs={12}>
						<Hidden only={['sm', 'xs', 'md']}>
							<Grid container={true} alignItems='center' style={{ height: '100%' }}>
								<Grid item={true} lg={7} md={7} sm={12} xs={12}>
									{/* Filter Button */}
									<Tooltip title={intlService.GetFormattedMessage('app.balance-history.more-filters')}>
										<IconButton
											onClick={() => {
												this.setState({
													filterPanelExpanded: !this.state.filterPanelExpanded,
												});
											}}
										>
											<Icon name='bars-horizontal-funnel-down' style={{ transform: 'translateY(-2px)' }} />
										</IconButton>
									</Tooltip>

									{/* Search Button */}
									<CustomButton
										fullWidth={false}
										type={ButtonTypeEnum.Button}
										buttonText={intlService.GetFormattedMessage('app.common.search')}
										onClick={() => {
											if (this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3) {
												return;
											}
											this.loadData();
										}}
									/>
								</Grid>
							</Grid>
						</Hidden>
						<Hidden only={['xl', 'lg']}>
							<Grid container={true} alignItems='center' spacing={3} style={{ height: '100%' }}>
								{/* Filter Button */}
								<Grid item={true}>
									<Button
										variant='outlined'
										color='primary'
										onClick={() => {
											this.setState({
												filterPanelExpanded: !this.state.filterPanelExpanded,
											});
										}}
									>
										<Icon name='bars-horizontal-funnel-down' style={{ transform: 'translateY(-2px)' }} />
										<span className='pl-1'>{intlService.GetFormattedMessage('app.balance-history.more-filters')}</span>
									</Button>
								</Grid>

								{/* Search Button */}
								<Grid item={true}>
									<CustomButton
										fullWidth={false}
										type={ButtonTypeEnum.Button}
										buttonText={intlService.GetFormattedMessage('app.common.search')}
										onClick={() => {
											if (this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3) {
												return;
											}
											this.loadData();
										}}
									/>
								</Grid>
							</Grid>
						</Hidden>
					</Grid>
					<Grid item={true} lg={8} md={12} sm={12} xs={12}>
						<Collapse in={this.state.filterPanelExpanded}>
							<Grid container={true} spacing={2}>
								{/* Manifest status filter*/}
								<Grid item={true} lg={4} md={4} sm={12} xs={12}>
									<FormControl fullWidth={true}>
										<TextField
											label={intlService.GetFormattedMessage('app.manifest.filter.manifest-status')}
											select={true}
											value={this.state.selectedManifestStatusFilterValues}
											onChange={(e: React.ChangeEvent) => {
												this.filterHelperFunc(
													e,
													[...this.manifestStatusFilter.manifestStatus],
													'selectedManifestStatusFilterValues',
													'selectAllManifestStatusSelection'
												);
											}}
											inputProps={{
												'data-testid': 'manifest.filter.manifest-status',
											}}
											SelectProps={{
												open: this.state.isOpenManifestStatusFilter,
												multiple: true,
												onClose: async () => {
													this.setState({
														...this.state,
														...this.prevFiltersInfo,
														isOpenManifestStatusFilter: false,
													});
												},
												onOpen: () =>
													this.setState({
														isOpenManifestStatusFilter: !this.state.isOpenManifestStatusFilter,
													}),
												renderValue: (selected) => {
													return this.state.selectAllManifestStatusSelection
														? intlService.GetFormattedMessage('app.shipment.all-label')
														: (selected as string[]).map((x) => x).join(', ');
												},
											}}
											variant='outlined'
										>
											<MenuItem
												key={intlService.GetFormattedMessage('app.filter-select-all')}
												value={intlService.GetFormattedMessage('app.filter-select-all')}
											>
												<Checkbox checked={this.state.selectAllManifestStatusSelection} />
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-select-all')} />
											</MenuItem>
											{this.manifestStatusFilter.manifestStatus.map((x) => (
												<MenuItem key={x} value={x} disabled={this.state.disabledCheckBox[x]}>
													<Checkbox checked={this.state.selectedManifestStatusFilterValues.includes(x)} />
													<ListItemText primary={x} />
												</MenuItem>
											))}
											<MenuItem className='text-center' value={intlService.GetFormattedMessage('app.filter-done')}>
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-done')} />
											</MenuItem>
										</TextField>
									</FormControl>
								</Grid>

								{/* Tracking number filter */}
								<Grid item={true} lg={4} md={4} sm={12} xs={12}>
									<TextField
										label={intlService.GetFormattedMessage('app.shipment.tracking-number')}
										placeholder={intlService.GetFormattedMessage('app.shipment.min-tracking-number-placeholder')}
										fullWidth={true}
										value={this.state.trackingNumber}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											this.setState({
												trackingNumber: e.target.value,
											});
										}}
										variant='outlined'
										error={this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3}
										helperText={
											this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3
												? intlService.GetFormattedMessage('app.shipment.min-tracking-number-helper-text')
												: null
										}
									/>
								</Grid>
							</Grid>
						</Collapse>
					</Grid>
				</Grid>
			</>
		);
	}

	private loadData = async () => {
		try {
			const { currentPage, startDate, endDate, selectedManifestStatusFilterValues, trackingNumber } = this.state;

			let pageToken = sharedService.generatePaginationToken(currentPage - 2);

			if (currentPage === 0) {
				pageToken = undefined;
			}

			this.setState({
				dataLoading: true,
			});
			const shipmentData = await adminShipmentService.GetShipmentManifestData(
				pageToken,
				startDate,
				endDate,
				selectedManifestStatusFilterValues,
				trackingNumber,
				this.props.axiosCancellationToken
			);
			this.setState({
				...shipmentData,
				dataLoading: false,
				isFilterApplied: false,
			});
		} catch (e) {
			if (!Axios.isCancel(e)) {
				this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	};

	private loadDataForPredefinedFilters = (startDate: Date, endDate: Date, filterValue: PredefinedFiltersEnum) => {
		this.setState({
			currentPage: 1,
			startDate,
			endDate,
			disableDatePickerFilter: filterValue !== PredefinedFiltersEnum.Custom,
		});
	};

	private shouldFetchData = (prevState: models.IShipmentManifestComponentState): boolean => {
		return prevState.currentPage !== this.state.currentPage && !this.props.isRequestCancelled;
	};

	private setStateAfterCancellingActiveRequests = (key: FilterType, value: any) => {
		const composedFunc = this.callAfterCancellingActiveRequests(() => {
			this.setState((prevState: models.IShipmentManifestComponentState) => ({
				...prevState,
				currentPage: 1,
				[key]: value,
			}));
		});

		composedFunc();
	};

	private callAfterCancellingActiveRequests = (func: (...args: any) => void) => {
		return async (...args: any) => {
			await this.props.cancelActiveRequests();
			func(...args);
		};
	};
}
export default withSnackBar(withAxiosCancellation(ShipmentManifestComponent));

const initialAdminShipmentManifestFilter = (): models.IAdminShipmentManifestFilters => ({
	manifestStatus: [
		ShipmentManifestStatusEnum.Rejected,
		ShipmentManifestStatusEnum.Reattempted,
		ShipmentManifestStatusEnum.AcceptedWithWarning,
		ShipmentManifestStatusEnum.ReadyToReattempt,
	],
});



// WEBPACK FOOTER //
// ./src/components/admin/manifest/shipment-manifest.component.tsx